import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Plus, Edit, Trash2, Search, Image, Archive, Package
} from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

export const AdminProducts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    images: [''],
    stock: '',
    featured: false,
    specifications: {}
  });

  const token = localStorage.getItem('adminToken');
  const userRole = localStorage.getItem('userRole');

  const normalizeImageUrl = (input) => {
    const raw = (input || '').trim();
    if (!raw) return '';

    // Imgur album links are not direct images (won't render in <img>)
    if (/^https?:\/\/imgur\.com\/a\//i.test(raw) || /^https?:\/\/imgur\.com\/gallery\//i.test(raw)) {
      return null;
    }

    // Convert imgur page to direct image URL when possible
    // Examples:
    // - https://imgur.com/AbB1f7C -> https://i.imgur.com/AbB1f7C.jpg
    // - https://imgur.com/AbB1f7C.png -> https://i.imgur.com/AbB1f7C.png
    const m = raw.match(/^https?:\/\/imgur\.com\/([A-Za-z0-9]+)(\.(jpg|jpeg|png|gif|webp))?$/i);
    if (m) {
      const id = m[1];
      const ext = (m[2] || '.jpg').toLowerCase();
      return `https://i.imgur.com/${id}${ext}`;
    }

    // Accept direct image URLs
    if (/^https?:\/\//i.test(raw) && /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(raw)) {
      return raw;
    }

    // Other non-direct links
    return null;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = (params.get('q') || '').trim();
    if (q) setSearchTerm(q);
  }, [location.search]);

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    // Apenas admin ou programador pode acessar
    if (userRole !== 'programmer' && userRole !== 'admin') {
      navigate('/admin');
      return;
    }
    fetchProducts();
    fetchCategories();
    const interval = setInterval(() => {
      fetchProducts();
    }, 30000);
    return () => clearInterval(interval);
  }, [userRole, navigate, token]);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const rawImage = formData.images?.[0] || '';
      const normalizedImage = normalizeImageUrl(rawImage);
      if (rawImage.trim() !== '' && !normalizedImage) {
        alert(
          'URL de imagem inválida.\n\n' +
          'Use um link direto de imagem (termina com .jpg/.png/.webp etc).\n' +
          'No Imgur, use um link como: https://imgur.com/AbB1f7C (sem /a/) e eu converto automaticamente.'
        );
        return;
      }

      const data = {
        ...formData,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        images: normalizedImage ? [normalizedImage] : []
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
      } else {
        await axios.post(`${API}/products`, data, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
      }

      setIsDialogOpen(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Erro ao salvar produto');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category_id: product.category_id,
      images: product.images.length > 0 ? product.images : [''],
      stock: product.stock.toString(),
      featured: product.featured,
      specifications: product.specifications || {}
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) return;
    
    try {
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Erro ao excluir produto');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category_id: '',
      images: [''],
      stock: '',
      featured: false,
      specifications: {}
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const { darkMode, theme } = useAdminTheme();

  return (
    <AdminLayout title="Produtos" subtitle="Gerencie os produtos da loja">
      {/* Header Actions */}
      <div data-tour="products-header" className="flex items-center justify-between mb-6">
        <div className="relative max-w-md flex-1">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${theme.textMuted}`} />
          <Input
            placeholder="Buscar produtos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`pl-10 ${theme.input} rounded-xl`}
          />
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setEditingProduct(null); resetForm(); }} className="bg-blue-600 hover:bg-blue-700 ml-4">
              <Plus className="h-4 w-4 mr-2" /> Novo Produto
            </Button>
          </DialogTrigger>
          <DialogContent className={`${theme.card} border ${theme.text} max-w-2xl max-h-[90vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle className={theme.text}>{editingProduct ? 'Editar Produto' : 'Novo Produto'}</DialogTitle>
              <DialogDescription className={theme.textMuted}>
                Preencha os dados do produto
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={theme.text}>Nome</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className={theme.input}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className={theme.text}>Categoria</Label>
                  <Select value={formData.category_id} onValueChange={(v) => setFormData({...formData, category_id: v})}>
                    <SelectTrigger className={theme.input}>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent className={theme.dropdown}>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className={theme.text}>Descrição</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className={theme.input}
                  rows={3}
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={theme.text}>Preço (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className={theme.input}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className={theme.text}>Estoque</Label>
                  <Input
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({...formData, stock: e.target.value})}
                    className={theme.input}
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className={theme.text}>URL da Imagem</Label>
                <Input
                  value={formData.images[0]}
                  onChange={(e) => setFormData({...formData, images: [e.target.value]})}
                  className={theme.input}
                  placeholder="https://..."
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  checked={formData.featured}
                  onCheckedChange={(checked) => setFormData({...formData, featured: checked})}
                />
                <Label className={theme.text}>Produto em Destaque</Label>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingProduct ? 'Salvar' : 'Criar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Total de Produtos</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{products.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
              <Archive className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Em Estoque</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{products.reduce((acc, p) => acc + (p.stock || 0), 0)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
              <Image className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Em Destaque</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{products.filter(p => p.featured).length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map(product => (
            <Card key={product.id} className={`${theme.card} border rounded-2xl overflow-hidden`}>
              <div className={`aspect-square ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} relative`}>
                {product.images && product.images[0] ? (
                  <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className={`h-12 w-12 ${theme.textMuted}`} />
                  </div>
                )}
                {product.featured && (
                  <Badge className="absolute top-2 right-2 bg-amber-500 text-white">Destaque</Badge>
                )}
              </div>
              <CardContent className="p-4">
                <h3 className={`${theme.text} font-semibold truncate`}>{product.name}</h3>
                <p className={`${theme.textMuted} text-sm truncate`}>{product.description}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-blue-600 font-bold">{formatCurrency(product.price)}</span>
                  <span className={`${theme.textMuted} text-sm flex items-center`}>
                    <Archive className="h-4 w-4 mr-1" /> {product.stock}
                  </span>
                </div>
                <div className="flex space-x-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(product)} className="flex-1">
                    <Edit className="h-4 w-4 mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(product.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};
